import { useCallback, useEffect, useRef, useState } from "react"

export interface TvSlide {
  id: string
  type: "hero" | "today" | "curated_place" | "essentials"
  data: Record<string, any>
  duration?: number
}

interface SlideEngineState {
  currentIndex: number
  nextIndex: number
  phase: "visible" | "fading"
}

interface UseSlideEngineOptions {
  slides: TvSlide[]
  defaultDuration: number
  onRefreshRequest?: () => void
  refreshIntervalMs?: number
}

export function useSlideEngine({
  slides,
  defaultDuration,
  onRefreshRequest,
  refreshIntervalMs = 30 * 60 * 1000,
}: UseSlideEngineOptions) {
  const [state, setState] = useState<SlideEngineState>({
    currentIndex: 0,
    nextIndex: 1,
    phase: "visible",
  })
  const [isPaused, setIsPaused] = useState(false)

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const refreshRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fadingRef = useRef(false)
  const slideCount = slides.length

  // Core navigation — handles both forward and backward with same fade logic
  // Guard: rejects calls during fading phase to prevent index corruption from rapid key presses
  const goToSlide = useCallback(
    (direction: 1 | -1) => {
      if (slideCount <= 1) return
      if (fadingRef.current) return

      fadingRef.current = true
      setState((prev) => ({ ...prev, phase: "fading" }))

      setTimeout(() => {
        setState((prev) => {
          const target = (prev.currentIndex + direction + slideCount) % slideCount
          const afterTarget = (target + 1) % slideCount
          return { currentIndex: target, nextIndex: afterTarget, phase: "visible" }
        })
        fadingRef.current = false
      }, 1400) // match CSS transition duration
    },
    [slideCount],
  )

  // Manual navigation — pauses auto-rotation with 30s auto-resume
  const goNext = useCallback(() => {
    goToSlide(1)
    setIsPaused(true)
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current)
    resumeTimerRef.current = setTimeout(() => setIsPaused(false), 30_000)
  }, [goToSlide])

  const goPrev = useCallback(() => {
    goToSlide(-1)
    setIsPaused(true)
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current)
    resumeTimerRef.current = setTimeout(() => setIsPaused(false), 30_000)
  }, [goToSlide])

  // Explicit pause toggle — cancels any auto-resume timer
  const togglePause = useCallback(() => {
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current)
    setIsPaused((prev) => !prev)
  }, [])

  // Slide rotation timer — skips scheduling when paused
  useEffect(() => {
    if (slideCount <= 1) return
    if (isPaused) return

    const currentSlideObj = slides[state.currentIndex]
    const duration = (currentSlideObj?.duration || defaultDuration) * 1000

    if (state.phase === "visible") {
      timerRef.current = setTimeout(() => goToSlide(1), duration)
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [state.currentIndex, state.phase, slides, defaultDuration, goToSlide, slideCount, isPaused])

  // Data refresh interval
  useEffect(() => {
    if (!onRefreshRequest) return
    refreshRef.current = setInterval(onRefreshRequest, refreshIntervalMs)
    return () => {
      if (refreshRef.current) clearInterval(refreshRef.current)
    }
  }, [onRefreshRequest, refreshIntervalMs])

  // Reset index if slides change length
  useEffect(() => {
    setState((prev) => {
      if (prev.currentIndex >= slideCount) {
        return { currentIndex: 0, nextIndex: slideCount > 1 ? 1 : 0, phase: "visible" }
      }
      return prev
    })
  }, [slideCount])

  // Cleanup resume timer on unmount
  useEffect(() => {
    return () => {
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current)
    }
  }, [])

  return {
    currentSlide: slides[state.currentIndex] || null,
    nextSlide: slides[state.nextIndex] || null,
    phase: state.phase,
    currentIndex: state.currentIndex,
    total: slideCount,
    goNext,
    goPrev,
    togglePause,
    isPaused,
  }
}
